import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import { subsetGetter, SubsetGetterProps, Subsets, subsetSetter } from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import cloneDeep from "lodash/cloneDeep";

/**
 * Används för att automatiskt generera funktioner och för typning av state
 */
const collectionNames = {
    administreringsmetoder: "administreringsmetoder",
    administreringsvagar: "administreringsvagar",
    administreringsstallen: "administreringsstallen",
    preciseringar: "preciseringar",
    medicintekniskaProdukter: "medicintekniskaProdukter",
    behandlingsorsaker: "behandlingsorsaker",
    tidsenheter: "tidsenheter",
};

export type Operationskod = {
    id: number;
    code: string;
    displayName: string;
    valid_from_date: string;
    valid_to_date: string;
    avsnitt?: string;
    beskrivning?: string;
    kapitel?: string;
    klassdel?: number;
    is_valid_now: boolean;
};

export type OperationskodDisplay = {
    value: Operationskod["id"];
    label: string;
};

type State = BaseState & {
    operationskoder: Map<Operationskod["id"], Operationskod>;
};

export const convertOperationskodData: (args: { operationskod?: Operationskod }) => OperationskodDisplay | null = ({
    operationskod,
}) => {
    if (!operationskod) {
        return null;
    }

    return {
        value: operationskod.id,
        label: `${operationskod.code} ${operationskod.displayName}`,
    };
};

/**
 * Varje default state ska innehålla BaseState + det som är specifikt för den här modulen.
 * Se helpers/Basestate
 */
const getDefaultState: () => State = () => {
    return {
        operationskoder: new Map(),
        ...cloneDeep(baseState),
    };
};

const moduleName = "operationskoder";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        operationskoder: (state: State) => [...state.operationskoder.values()],
        operationskodSubset: (state: State) => (props: SubsetGetterProps<State> & { displayMode?: boolean }) => {
            const result = subsetGetter<State>({
                ...props,
                state,
                limit: 0,
                collectionName: moduleName,
            });

            if (props.displayMode && Array.isArray(result)) {
                return result.map((singleResult) => convertOperationskodData({ operationskod: singleResult }));
            }

            return result;
        },
        operationskodById:
            (state: State) =>
            ({ id }: { id?: number }) => {
                if (!id) {
                    return null;
                }

                return state.operationskoder.get(id);
            },
    },
    /**
     * Mutations ska alltid innehålla baseMutations + det som är specifikt för modulen
     */
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());

                resetDelayedResetTimeout({ moduleName });
            },
        }),
        addOperationskoder: (
            state: State,
            {
                offset,
                resetSubset,
                subsetName = "default",
                operationskoder = [],
            }: {
                resetSubset?: boolean;
                offset: number;
                subsetName: keyof Subsets;
                operationskoder: Operationskod[];
            }
        ) =>
            subsetSetter<State, Operationskod>({
                offset,
                subsetName,
                state,
                resetSubset,
                collectionName: moduleName,
                items: operationskoder,
            }),
    },
    actions: {
        ...baseActions({
            moduleName,
        }),
        fetchOperationskoder: async (
            { commit, state }: BaseActionsProps<State>,
            {
                displayMode,
                filterParams = {},
                config = {},
            }: {
                offset?: number;
                filterParams?: FilterParams;
                config?: {
                    tabbnamn?: string;
                };
                displayMode?: boolean;
            }
        ) => {
            try {
                const {
                    subsetName,
                    results,
                    total,
                    nextPage,
                    offset: resultOffset,
                } = await fetchItems<State, Operationskod>({
                    filterParams,
                    state,
                    config,
                    page: 1,
                    limit: 0,
                    collectionName: moduleName,
                    apiUrl: "operation/kodverk/operationskoder/",
                });

                if (!results) {
                    return {
                        subsetName,
                        total,
                        nextPage,
                    };
                }

                commit("addOperationskoder", {
                    subsetName,
                    offset: resultOffset,
                    operationskoder: results,
                });

                return {
                    total,
                    nextPage,
                    subsetName,
                    results: displayMode
                        ? results.map((singleResult) => convertOperationskodData({ operationskod: singleResult }))
                        : results,
                };
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
    },
};
